import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';

const MatchScoreModal = ({ 
    isOpen, 
    onClose, 
    matchId, 
    opponentName, 
    onSubmitScore, 
    teamMembers = [], 
    opponentMembers = [], 
    loadingOpponentMembers = false,
    modalState = 'submit', // 'submit', 'verify', 'view', 'disputed_resubmit'
    existingScores = null,
    onVerifyScore = null,
    onDisputeScore = null,
    onResubmitScore = null,
    isOpponentView = false, // New prop to indicate if this is from opponent's perspective
    isIndividualMatch = false // Individual player match: show "You" vs opponent, same scoring rules
}) => {
    const { t } = useTranslation();
    const [scores, setScores] = useState(() => {
        if (existingScores && (modalState === 'verify' || modalState === 'view' || modalState === 'disputed_resubmit')) {
            // If this is the opponent's view, swap the scores
            if (isOpponentView) {
                return {
                    team1_set1: existingScores.team2_set1 || '',
                    team1_set2: existingScores.team2_set2 || '',
                    team1_set3: existingScores.team2_set3 || '',
                    team2_set1: existingScores.team1_set1 || '',
                    team2_set2: existingScores.team1_set2 || '',
                    team2_set3: existingScores.team1_set3 || ''
                };
            } else {
                return {
                    team1_set1: existingScores.team1_set1 || '',
                    team1_set2: existingScores.team1_set2 || '',
                    team1_set3: existingScores.team1_set3 || '',
                    team2_set1: existingScores.team2_set1 || '',
                    team2_set2: existingScores.team2_set2 || '',
                    team2_set3: existingScores.team2_set3 || ''
                };
            }
        }
        return {
            team1_set1: '',
            team1_set2: '',
            team1_set3: '',
            team2_set1: '',
            team2_set2: '',
            team2_set3: ''
        };
    });

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            // Store the current scroll position
            const scrollY = window.scrollY;
            // Lock the body scroll
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
            
            return () => {
                // Restore scroll when modal closes
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflow = '';
                window.scrollTo(0, scrollY);
            };
        }
    }, [isOpen]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Check if match is tied 1-1 after first two sets (3rd set should be enabled)
    const isMatchTied = React.useMemo(() => {
        const team1_set1 = parseInt(scores.team1_set1) || 0;
        const team1_set2 = parseInt(scores.team1_set2) || 0;
        const team2_set1 = parseInt(scores.team2_set1) || 0;
        const team2_set2 = parseInt(scores.team2_set2) || 0;
        
        // Both sets must have valid scores
        if (scores.team1_set1 === '' || scores.team1_set2 === '' || 
            scores.team2_set1 === '' || scores.team2_set2 === '') {
            return false;
        }
        
        // Calculate sets won
        let team1SetsWon = 0;
        let team2SetsWon = 0;
        
        if (team1_set1 > team2_set1) team1SetsWon++;
        else if (team2_set1 > team1_set1) team2SetsWon++;
        
        if (team1_set2 > team2_set2) team1SetsWon++;
        else if (team2_set2 > team1_set2) team2SetsWon++;
        
        // 3rd set is only enabled if match is tied 1-1
        return team1SetsWon === 1 && team2SetsWon === 1;
    }, [scores.team1_set1, scores.team1_set2, scores.team2_set1, scores.team2_set2]);

    const handleScoreChange = (set, team, value) => {
        // Don't allow changes in view mode
        if (modalState === 'view') return;
        
        // Block 3rd set changes if match is not tied 1-1
        if (set === 3 && !isMatchTied) {
            return;
        }
        
        // Only allow numbers and limit to Padel score range (0-30)
        if (value === '' || (value >= 0 && value <= 30)) {
            setScores(prev => {
                const newScores = {
                    ...prev,
                    [`team${team}_set${set}`]: value
                };
                
                // If changing set 1 or 2, and match is no longer tied, clear 3rd set scores
                if ((set === 1 || set === 2) && value !== '') {
                    const team1_set1 = set === 1 && team === 1 ? parseInt(value) : parseInt(newScores.team1_set1) || 0;
                    const team1_set2 = set === 2 && team === 1 ? parseInt(value) : parseInt(newScores.team1_set2) || 0;
                    const team2_set1 = set === 1 && team === 2 ? parseInt(value) : parseInt(newScores.team2_set1) || 0;
                    const team2_set2 = set === 2 && team === 2 ? parseInt(value) : parseInt(newScores.team2_set2) || 0;
                    
                    // Check if match is still tied
                    let team1SetsWon = 0;
                    let team2SetsWon = 0;
                    
                    if (team1_set1 > team2_set1) team1SetsWon++;
                    else if (team2_set1 > team1_set1) team2SetsWon++;
                    
                    if (team1_set2 > team2_set2) team1SetsWon++;
                    else if (team2_set2 > team1_set2) team2SetsWon++;
                    
                    // If not tied 1-1, clear 3rd set
                    if (!(team1SetsWon === 1 && team2SetsWon === 1)) {
                        newScores.team1_set3 = '';
                        newScores.team2_set3 = '';
                    }
                }
                
                return newScores;
            });
        }
    };

    const validateScores = () => {
        const { team1_set1, team1_set2, team1_set3, team2_set1, team2_set2, team2_set3 } = scores;
        
        // Check if all scores are filled (note: 0 is a valid score, so check for empty string or null)
        if (team1_set1 === '' || team1_set2 === '' || team2_set1 === '' || team2_set2 === '') {
            setError(t('matchScoreModal.valFillAllSets'));
            return false;
        }

        // Check if scores are within valid range (0-30)
        const scoresArray = [team1_set1, team1_set2, team1_set3, team2_set1, team2_set2, team2_set3];
        const validScores = scoresArray.filter(score => score !== '').map(score => parseInt(score));
        
        if (validScores.some(score => isNaN(score) || score < 0 || score > 30)) {
            setError(t('matchScoreModal.valScoresRange'));
            return false;
        }

        // Check if at least one team won 2 sets
        let team1SetsWon = 0;
        let team2SetsWon = 0;

        if (parseInt(team1_set1) > parseInt(team2_set1)) team1SetsWon++;
        else if (parseInt(team2_set1) > parseInt(team1_set1)) team2SetsWon++;

        if (parseInt(team1_set2) > parseInt(team2_set2)) team1SetsWon++;
        else if (parseInt(team2_set2) > parseInt(team1_set2)) team2SetsWon++;

        // Only count 3rd set if match was tied 1-1 after first two sets
        // Check if match was tied before 3rd set
        const wasTiedBeforeSet3 = team1SetsWon === 1 && team2SetsWon === 1;
        
        // If we have a third set and match was tied, check it
        if (wasTiedBeforeSet3 && 
            team1_set3 !== '' && team1_set3 !== null && team1_set3 !== undefined && 
            team2_set3 !== '' && team2_set3 !== null && team2_set3 !== undefined) {
            const set3_team1 = parseInt(team1_set3);
            const set3_team2 = parseInt(team2_set3);
            if (set3_team1 > set3_team2) team1SetsWon++;
            else if (set3_team2 > set3_team1) team2SetsWon++;
        }

        // Check if someone won
        if (team1SetsWon < 2 && team2SetsWon < 2) {
            setError(t('matchScoreModal.valWinTwoSets'));
            return false;
        }

        // Check if both teams won 2 sets (invalid)
        if (team1SetsWon === 2 && team2SetsWon === 2) {
            setError(t('matchScoreModal.valBothTwoSetsInvalid'));
            return false;
        }

        setError(null);
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (modalState === 'view') {
            onClose();
            return;
        }
        
        if (modalState === 'submit' && !validateScores()) {
            return;
        }

        setLoading(true);
        try {
            if (modalState === 'submit') {
                // Prepare scores - clear 3rd set if match is not tied 1-1
                const scoresToSubmit = { ...scores };
                
                // If match is won 2-0, ensure 3rd set is empty/null
                if (!isMatchTied) {
                    scoresToSubmit.team1_set3 = '';
                    scoresToSubmit.team2_set3 = '';
                }
                
                await onSubmitScore(scoresToSubmit);
            }
            onClose();
        } catch (err) {
            const raw = String(err?.message || '').toLowerCase();
            if (raw.includes('already submitted')) {
                setError(t('matchScoreModal.errAlreadySubmitted'));
            } else if (err?.message) {
                setError(err.message);
            } else {
                setError(t('matchScoreModal.errSubmitFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (action) => {
        setLoading(true);
        try {
            if (action === 'confirm' && onVerifyScore) {
                await onVerifyScore();
            } else if (action === 'dispute' && onDisputeScore) {
                await onDisputeScore();
            }
            onClose();
        } catch (err) {
            const raw = String(err?.message || '').toLowerCase();
            if (action === 'confirm' && raw.includes('already processed')) {
                setError(t('matchScoreModal.errVerifyAlreadyDone'));
            } else if (err?.message) {
                setError(err.message);
            } else {
                setError(
                    action === 'confirm'
                        ? t('matchScoreModal.errVerifyConfirmFailed')
                        : t('matchScoreModal.errVerifyDisputeFailed'),
                );
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Overlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <HeaderContent>
                        <Title>{t('matchScoreModal.titleSubmitResults')}</Title>
                        <Subtitle>{t('matchScoreModal.subtitleVs', { name: opponentName })}</Subtitle>
                        <InstructionText>
                            {!isIndividualMatch && (
                                <>
                                    {t('matchScoreModal.instructionConfirmFlow')}
                                    <br />
                                </>
                            )}
                            <Trans i18nKey="matchScoreModal.instructionScoring" components={{ strong: <strong /> }} />
                        </InstructionText>
                    </HeaderContent>
                    <CloseButton onClick={onClose}>×</CloseButton>
                </ModalHeader>

                <FormContainer>
                    <ScoreboardSection>
                        <SetHeaders>
                            <SetHeader>{isIndividualMatch ? t('matchScoreModal.headerSide') : t('matchScoreModal.headerMembers')}</SetHeader>
                            <SetHeader>{t('matchScoreModal.set1')}</SetHeader>
                            <SetHeader>{t('matchScoreModal.set2')}</SetHeader>
                            <SetHeader>
                                {t('matchScoreModal.tieBreaker')}
                                {!isMatchTied && scores.team1_set1 !== '' && scores.team1_set2 !== '' && 
                                 scores.team2_set1 !== '' && scores.team2_set2 !== '' && (
                                    <span style={{ 
                                        fontSize: '14px', 
                                        display: 'inline-block', 
                                        color: '#9ca3af',
                                        fontWeight: 'normal',
                                        marginLeft: '4px'
                                    }}>
                                        🔒
                                    </span>
                                )}
                            </SetHeader>
                        </SetHeaders>

                        {/* Your Team / You Section */}
                        <TeamSection>
                            <TeamMembersContainer>
                                {isIndividualMatch ? (
                                    <MemberRow>
                                        <MemberInfo>
                                            <MemberAvatar><AvatarInitials>{t('matchScoreModal.you')}</AvatarInitials></MemberAvatar>
                                            <MemberDetails><MemberName>{t('matchScoreModal.you')}</MemberName></MemberDetails>
                                        </MemberInfo>
                                    </MemberRow>
                                ) : (
                                    <>
                                        <MemberRow>
                                            <MemberInfo>
                                                <MemberAvatar>
                                                    {teamMembers[0]?.profile_picture_url ? (
                                                        <AvatarImage src={teamMembers[0].profile_picture_url} alt={teamMembers[0].first_name} />
                                                    ) : (
                                                        <AvatarInitials>
                                                            {teamMembers[0]?.first_name?.[0]}{teamMembers[0]?.last_name?.[0]}
                                                        </AvatarInitials>
                                                    )}
                                                </MemberAvatar>
                                                <MemberDetails>
                                                    <MemberName>{teamMembers[0]?.first_name} {teamMembers[0]?.last_name}</MemberName>
                                                    {teamMembers[0]?.position === 'captain' && <CaptainBadge>{t('teamDashboardPage.roleCaptain')}</CaptainBadge>}
                                                </MemberDetails>
                                            </MemberInfo>
                                        </MemberRow>
                                        <MemberRow>
                                            <MemberInfo>
                                                <MemberAvatar>
                                                    {teamMembers[1]?.profile_picture_url ? (
                                                        <AvatarImage src={teamMembers[1].profile_picture_url} alt={teamMembers[1].first_name} />
                                                    ) : (
                                                        <AvatarInitials>
                                                            {teamMembers[1]?.first_name?.[0]}{teamMembers[1]?.last_name?.[0]}
                                                        </AvatarInitials>
                                                    )}
                                                </MemberAvatar>
                                                <MemberDetails>
                                                    <MemberName>{teamMembers[1]?.first_name} {teamMembers[1]?.last_name}</MemberName>
                                                    {teamMembers[1]?.position === 'captain' && <CaptainBadge>{t('teamDashboardPage.roleCaptain')}</CaptainBadge>}
                                                </MemberDetails>
                                            </MemberInfo>
                                        </MemberRow>
                                    </>
                                )}
                            </TeamMembersContainer>
                            
                            {/* Score inputs spanning across both team members */}
                            <ScoreInputsContainer>
                                <ExpandedScoreInput
                                    type="number"
                                    min="0"
                                    max="30"
                                    value={scores.team1_set1}
                                    onChange={(e) => handleScoreChange(1, 1, e.target.value)}
                                    title={t('matchScoreModal.scoreTitleRange')}
                                    readOnly={modalState === 'view'}
                                />
                                <ExpandedScoreInput
                                    type="number"
                                    min="0"
                                    max="30"
                                    value={scores.team1_set2}
                                    onChange={(e) => handleScoreChange(2, 1, e.target.value)}
                                    title={t('matchScoreModal.scoreTitleRange')}
                                    readOnly={modalState === 'view'}
                                />
                                <ExpandedScoreInput
                                    type="number"
                                    min="0"
                                    max="30"
                                    value={scores.team1_set3}
                                    onChange={(e) => handleScoreChange(3, 1, e.target.value)}
                                    title={isMatchTied ? t('matchScoreModal.scoreTitleTiebreaker') : t('matchScoreModal.scoreTitleThirdLocked')}
                                    readOnly={modalState === 'view' || !isMatchTied}
                                    disabled={!isMatchTied}
                                    style={{
                                        opacity: isMatchTied ? 1 : 0.5,
                                        cursor: isMatchTied ? 'text' : 'not-allowed',
                                        backgroundColor: isMatchTied ? undefined : '#f3f4f6'
                                    }}
                                />
                            </ScoreInputsContainer>
                        </TeamSection>

                        {/* Opponent Team / Opponent Section */}
                        <TeamSection>
                            <TeamMembersContainer>
                                {isIndividualMatch ? (
                                    <MemberRow>
                                        <MemberInfo>
                                            <MemberAvatar><AvatarInitials>{((opponentName || t('matchScoreModal.opponentFallback')).split(' ').map(n => n[0]).filter(Boolean).join('') || 'Op').slice(0, 2)}</AvatarInitials></MemberAvatar>
                                            <MemberDetails><MemberName>{opponentName || t('matchScoreModal.opponentFallback')}</MemberName></MemberDetails>
                                        </MemberInfo>
                                    </MemberRow>
                                ) : loadingOpponentMembers ? (
                                    <>
                                        <MemberRow>
                                            <MemberInfo>
                                                <MemberAvatar><AvatarInitials>...</AvatarInitials></MemberAvatar>
                                                <MemberDetails><MemberName>{t('matchScoreModal.loadingMembers')}</MemberName></MemberDetails>
                                            </MemberInfo>
                                        </MemberRow>
                                        <MemberRow>
                                            <MemberInfo>
                                                <MemberAvatar><AvatarInitials>...</AvatarInitials></MemberAvatar>
                                                <MemberDetails><MemberName>{t('matchScoreModal.loadingMembers')}</MemberName></MemberDetails>
                                            </MemberInfo>
                                        </MemberRow>
                                    </>
                                ) : (
                                    <>
                                        <MemberRow>
                                            <MemberInfo>
                                                <MemberAvatar>
                                                    {opponentMembers[0]?.profile_picture_url ? (
                                                        <AvatarImage src={opponentMembers[0].profile_picture_url} alt={opponentMembers[0].first_name} />
                                                    ) : (
                                                        <AvatarInitials>
                                                            {opponentMembers[0]?.first_name?.[0]}{opponentMembers[0]?.last_name?.[0]}
                                                        </AvatarInitials>
                                                    )}
                                                </MemberAvatar>
                                                <MemberDetails>
                                                    <MemberName>{opponentMembers[0]?.first_name} {opponentMembers[0]?.last_name}</MemberName>
                                                    {opponentMembers[0]?.position === 'captain' && <CaptainBadge>{t('teamDashboardPage.roleCaptain')}</CaptainBadge>}
                                                </MemberDetails>
                                            </MemberInfo>
                                        </MemberRow>
                                        <MemberRow>
                                            <MemberInfo>
                                                <MemberAvatar>
                                                    {opponentMembers[1]?.profile_picture_url ? (
                                                        <AvatarImage src={opponentMembers[1].profile_picture_url} alt={opponentMembers[1].first_name} />
                                                    ) : (
                                                        <AvatarInitials>
                                                            {opponentMembers[1]?.first_name?.[0]}{opponentMembers[1]?.last_name?.[0]}
                                                        </AvatarInitials>
                                                    )}
                                                </MemberAvatar>
                                                <MemberDetails>
                                                    <MemberName>{opponentMembers[1]?.first_name} {opponentMembers[1]?.last_name}</MemberName>
                                                    {opponentMembers[1]?.position === 'captain' && <CaptainBadge>{t('teamDashboardPage.roleCaptain')}</CaptainBadge>}
                                                </MemberDetails>
                                            </MemberInfo>
                                        </MemberRow>
                                    </>
                                )}
                            </TeamMembersContainer>
                            
                            {/* Score inputs spanning across both opponent team members */}
                            <ScoreInputsContainer>
                                <ExpandedScoreInput
                                    type="number"
                                    min="0"
                                    max="30"
                                    value={scores.team2_set1}
                                    onChange={(e) => handleScoreChange(1, 2, e.target.value)}
                                    title={t('matchScoreModal.scoreTitleRange')}
                                    readOnly={modalState === 'view'}
                                />
                                <ExpandedScoreInput
                                    type="number"
                                    min="0"
                                    max="30"
                                    value={scores.team2_set2}
                                    onChange={(e) => handleScoreChange(2, 2, e.target.value)}
                                    title={t('matchScoreModal.scoreTitleRange')}
                                    readOnly={modalState === 'view'}
                                />
                                <ExpandedScoreInput
                                    type="number"
                                    min="0"
                                    max="30"
                                    value={scores.team2_set3}
                                    onChange={(e) => handleScoreChange(3, 2, e.target.value)}
                                    title={isMatchTied ? t('matchScoreModal.scoreTitleTiebreaker') : t('matchScoreModal.scoreTitleThirdLocked')}
                                    readOnly={modalState === 'view' || !isMatchTied}
                                    disabled={!isMatchTied}
                                    style={{
                                        opacity: isMatchTied ? 1 : 0.5,
                                        cursor: isMatchTied ? 'text' : 'not-allowed',
                                        backgroundColor: isMatchTied ? undefined : '#f3f4f6'
                                    }}
                                />
                            </ScoreInputsContainer>
                        </TeamSection>
                    </ScoreboardSection>

                    {error && <ErrorMessage>{error}</ErrorMessage>}

                    <ButtonContainer>
                        {modalState === 'submit' && (
                            <>
                                <CheckButton type="button" onClick={() => {}}>
                                    {t('matchScoreModal.btnCheckResults')}
                                </CheckButton>
                                <SubmitButton 
                                    type="submit" 
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    {loading ? t('matchScoreModal.btnSubmitting') : t('matchScoreModal.btnSubmitResult')}
                                </SubmitButton>
                            </>
                        )}
                        
                        {modalState === 'verify' && (
                            <>
                                <SubmitButton 
                                    type="button" 
                                    onClick={() => handleVerify('dispute')}
                                    disabled={loading}
                                    style={{ background: 'transparent', color: '#d52b1e', border: '2px solid #d52b1e' }}
                                >
                                    {loading ? t('matchScoreModal.btnDisputing') : t('matchScoreModal.btnDisputeScore')}
                                </SubmitButton>
                                <SubmitButton 
                                    type="button" 
                                    onClick={() => handleVerify('confirm')}
                                    disabled={loading}
                                >
                                    {loading ? t('matchScoreModal.btnConfirming') : t('matchScoreModal.btnConfirmScore')}
                                </SubmitButton>
                            </>
                        )}
                        
                        {modalState === 'disputed_resubmit' && (
                            <SubmitButton 
                                type="button" 
                                onClick={onResubmitScore}
                                disabled={loading}
                                style={{ background: 'linear-gradient(135deg, #043663 0%, #0a4d7a 100%)' }}
                            >
                                {t('matchScoreModal.btnResubmitScore')}
                            </SubmitButton>
                        )}
                        
                        {modalState === 'view' && (
                            <SubmitButton 
                                type="button" 
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {t('matchScoreModal.btnClose')}
                            </SubmitButton>
                        )}
                    </ButtonContainer>
                </FormContainer>
            </ModalContainer>
        </Overlay>
    );
};

// Styled Components
const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 20px;
    
    @media (max-width: 768px) {
        padding: 0;
        align-items: flex-end;
    }
`;

const ModalContainer = styled.div`
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 600px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    
    @media (max-width: 768px) {
        max-height: 95vh;
        border-radius: 16px 16px 0 0;
        max-width: 100%;
        height: auto;
        min-height: 0;
    }
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 20px 24px;
    border-bottom: 2px solid #e0e0e0;
    background: linear-gradient(135deg, #043663 0%, #d52b1e 100%);
    color: white;
    
    @media (max-width: 768px) {
        padding: 12px 16px;
    }
`;

const HeaderContent = styled.div`
    flex: 1;
`;

const Title = styled.h2`
    margin: 0 0 8px 0;
    font-size: 24px;
    font-weight: 700;
    
    @media (max-width: 768px) {
        font-size: 18px;
        margin: 0 0 4px 0;
    }
`;

const Subtitle = styled.div`
    font-size: 16px;
    opacity: 0.95;
    font-weight: 500;
    margin-bottom: 8px;
    
    @media (max-width: 768px) {
        font-size: 13px;
        margin-bottom: 4px;
    }
`;

const InstructionText = styled.div`
    font-size: 12px;
    opacity: 0.8;
    line-height: 1.4;
    max-width: 400px;
    
    @media (max-width: 768px) {
        font-size: 10px;
        line-height: 1.3;
    }
`;

const CloseButton = styled.button`
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    font-size: 32px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    line-height: 1;
    padding: 0;
    margin-left: 16px;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: scale(1.1);
    }
`;

const FormContainer = styled.div`
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    
    @media (max-width: 768px) {
        padding: 16px;
        gap: 16px;
        max-height: calc(95vh - 100px); /* Account for header height */
    }
`;

const ScoreboardSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    
    @media (max-width: 768px) {
        gap: 8px;
    }
`;

const SetHeaders = styled.div`
    display: grid;
    grid-template-columns: 1fr 80px 80px 80px;
    gap: 16px;
    align-items: center;
    margin-bottom: 8px;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr 60px 60px 60px;
        gap: 12px;
    }
`;

const SetHeader = styled.div`
    font-weight: 600;
    color: #333;
    text-align: center;
    font-size: 14px;
    
    @media (max-width: 768px) {
        font-size: 12px;
    }
`;

const TeamSection = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
    
    &:last-child {
        margin-bottom: 0;
    }
    
    @media (max-width: 768px) {
        gap: 8px;
        margin-bottom: 12px;
    }
`;

const TeamMembersContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ScoreInputsContainer = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
    
    @media (max-width: 768px) {
        gap: 12px;
    }
`;

// eslint-disable-next-line no-unused-vars
const TeamHeader = styled.div`
    display: grid;
    grid-template-columns: 1fr 80px 80px 80px;
    gap: 16px;
    align-items: center;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr 60px 60px 60px;
        gap: 12px;
    }
`;

// eslint-disable-next-line no-unused-vars
const TeamName = styled.div`
    font-weight: 600;
    color: #333;
    font-size: 16px;
    
    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const MemberRow = styled.div`
    display: flex;
    align-items: center;
    padding: 4px 0;
`;

// eslint-disable-next-line no-unused-vars
const ScoreRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 80px 80px 80px;
    gap: 16px;
    align-items: center;
    padding: 8px 0;
    margin-top: 8px;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr 60px 60px 60px;
        gap: 12px;
    }
`;

// eslint-disable-next-line no-unused-vars
const MemberRowWithScore = styled.div`
    display: grid;
    grid-template-columns: 1fr 80px 80px 80px;
    gap: 16px;
    align-items: center;
    padding: 4px 0;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr 60px 60px 60px;
        gap: 12px;
    }
`;

// eslint-disable-next-line no-unused-vars
const ScoreLabel = styled.div`
    font-weight: 600;
    color: #333;
    font-size: 14px;
    text-align: left;
`;

const ExpandedScoreInput = styled.input`
    width: 80px;
    height: 80px;
    border: 3px solid #667eea;
    border-radius: 12px;
    text-align: center;
    font-size: 24px;
    font-weight: 700;
    color: #333;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);

    &:focus {
        outline: none;
        border-color: #4f46e5;
        box-shadow: 0 6px 20px rgba(79, 70, 229, 0.25);
        transform: translateY(-2px);
        background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    }

    &:hover {
        border-color: #4f46e5;
        box-shadow: 0 5px 16px rgba(102, 126, 234, 0.2);
        transform: translateY(-1px);
    }

    &:invalid {
        border-color: #ef4444;
        background: linear-gradient(135deg, #fef2f2 0%, #ffffff 100%);
        color: #dc2626;
        box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
    }

    &:valid {
        border-color: #10b981;
        background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
    }
    
    @media (max-width: 768px) {
        width: 70px;
        height: 70px;
        font-size: 20px;
    }
`;

const MemberInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 1;
    
    @media (max-width: 768px) {
        gap: 8px;
    }
`;

const MemberAvatar = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(135deg, #043663 0%, #d52b1e 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const AvatarImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const AvatarInitials = styled.div`
    color: white;
    font-weight: 600;
    font-size: 14px;
`;

const MemberDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
`;

const MemberName = styled.div`
    font-weight: 600;
    color: #1f2937;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 120px;
    
    @media (max-width: 768px) {
        max-width: 100px;
        font-size: 13px;
    }
`;

const CaptainBadge = styled.span`
    background: #fbbf24;
    color: #92400e;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 10px;
    text-transform: uppercase;
`;

// eslint-disable-next-line no-unused-vars
const ScoreInput = styled.input`
    width: 100%;
    height: 60px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    text-align: center;
    font-size: 20px;
    font-weight: 600;
    color: #333;
    background: white;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #667eea;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }

    &:hover {
        border-color: #ccc;
    }

    &:invalid {
        border-color: #ef4444;
        background-color: #fef2f2;
    }

    &:valid {
        border-color: #10b981;
    }
    
    @media (max-width: 768px) {
        height: 55px;
        font-size: 18px;
    }
`;

// eslint-disable-next-line no-unused-vars
const TeamScoreRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 80px 80px 80px;
    gap: 16px;
    align-items: center;
    padding: 8px 0;
    margin-top: -8px;
    position: relative;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr 60px 60px 60px;
        gap: 12px;
    }
`;

const ErrorMessage = styled.div`
    background: #fee;
    color: #c33;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 14px;
    text-align: center;
    border: 1px solid #fdd;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: auto;
    padding-top: 16px;
    border-top: 1px solid #e0e0e0;
    background: white;
    position: sticky;
    bottom: 0;
    
    @media (max-width: 768px) {
        gap: 8px;
        padding-top: 8px;
        position: sticky;
        bottom: 0;
        z-index: 10;
    }
`;

const CheckButton = styled.button`
    background: white;
    color: #043663;
    border: 2px solid #043663;
    padding: 12px 24px;
    border-radius: 24px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #f0f4ff;
        transform: translateY(-1px);
    }
    
    @media (max-width: 768px) {
        padding: 12px 20px;
        font-size: 14px;
    }
`;

const SubmitButton = styled.button`
    background: linear-gradient(135deg, #043663 0%, #d52b1e 100%);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 24px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 4px 12px rgba(4, 54, 99, 0.3);

    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(4, 54, 99, 0.4);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
    }
    
    @media (max-width: 768px) {
        padding: 12px 20px;
        font-size: 14px;
    }
`;

export default MatchScoreModal;
